import { useEffect, useRef, useState } from "react";

/**
 * El TELÓN de entrada, mismo que PILOT. Al iniciar sesión, Login deja la
 * bandera "radar_entrada" y App pinta esta capa: el gradiente de marca cubre
 * TODO (incluida la espera de los RPCs del perfil — sin él ahí hay un
 * parpadeo), la palomita se dibuja al centro, y cuando el perfil ya llegó Y la
 * palomita se disfrutó (mínimo ~1.1s), el telón se levanta con calma.
 *
 * `listo` también se enciende si el perfil FALLA: un telón que nunca se
 * levanta sería peor que ver la tarjeta de error.
 */
export function TelonEntrada({
  listo,
  onFin,
  esperarDatos = true,
}: {
  listo: boolean;
  onFin: () => void;
  /** false cuando `listo` viene de un error: ahí no habrá datos que esperar. */
  esperarDatos?: boolean;
}) {
  const [saliendo, setSaliendo] = useState(false);
  const inicio = useRef(Date.now());

  useEffect(() => {
    if (!listo || saliendo) return;
    const MINIMO = 1100;
    const ESPERA_MAX_DATOS = 2500;

    // Además del perfil, esperamos a que la primera vista tenga DATOS: si el
    // telón se levanta sobre esqueletos, al llegar los datos la vista se
    // vuelve a montar y se re-anima encima — que es justo lo que se veía
    // trabado al entrar. Con esto el telón descubre una pantalla ya hecha.
    // Señal POSITIVA de "ya hay pantalla": no basta con que no haya
    // esqueletos, porque justo cuando llega el perfil la vista todavía no se
    // ha montado y no hay ninguno — se daba por lista una pantalla vacía y el
    // telón se levantaba sobre los esqueletos que aparecían un instante
    // después. Hay que exigir contenido de verdad.
    const contenidoListo = () => {
      const main = document.querySelector("main");
      if (!main) return false;
      if (main.querySelector(".esqueleto")) return false;
      return !!(
        main.querySelector(".tile, .kpi") ||
        main.querySelector("table.data") ||
        main.querySelector("svg:not(.ic)") ||
        main.querySelector(".vacio") ||
        main.querySelector(".aviso-error")
      );
    };

    let mo: MutationObserver | null = null;
    let tMin: number | undefined;
    let tTope: number | undefined;

    const levantar = () => {
      if (mo) { mo.disconnect(); mo = null; }
      window.clearTimeout(tTope);
      const falta = Math.max(0, MINIMO - (Date.now() - inicio.current));
      tMin = window.setTimeout(() => setSaliendo(true), falta);
    };

    if (!esperarDatos || contenidoListo()) {
      levantar();
    } else {
      mo = new MutationObserver(() => { if (contenidoListo()) levantar(); });
      mo.observe(document.body, { childList: true, subtree: true });
      // tope duro: si los datos tardan, más vale enseñar el esqueleto que
      // dejar a la persona mirando el telón
      tTope = window.setTimeout(levantar, ESPERA_MAX_DATOS);
    }

    return () => {
      if (mo) mo.disconnect();
      window.clearTimeout(tMin);
      window.clearTimeout(tTope);
    };
  }, [listo, saliendo, esperarDatos]);

  useEffect(() => {
    if (!saliendo) return;
    const t = setTimeout(onFin, 750); // lo que dura telon-sube en el CSS
    return () => clearTimeout(t);
  }, [saliendo, onFin]);

  return (
    <div className={"telon" + (saliendo ? " telon-saliendo" : "")} aria-hidden="true">
      <svg
        className="telon-check" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.4"
        strokeLinecap="round" strokeLinejoin="round"
      >
        <path d="M5 12.5l4.5 4.5L19 7.5" />
      </svg>
    </div>
  );
}

/** Bandera que deja el login para que App sepa que hay que pintar el telón. */
export const BANDERA_ENTRADA = "radar_entrada";
